import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "./api/client";
import AdvancedFiltersModal from "./components/AdvancedFiltersModal";
import AccountSettingSelect from "./components/AccountSettingSelect";
import BrowseCardsResultsTable from "./components/BrowseCardsResultsTable";
import { renderConditionOption, renderEditionOption, renderLanguageOption } from "./components/dropdownOptionRenderers";
import { useCurrentUser } from "./currentUserContext";
import usePaginationScroll from "./utils/usePaginationScroll";
import { addTradeDraftRequestCard, getRequestedTradeDraftQty, mergeTradeDraftRequestCards, serializeTradeDraftCardForRequest } from "./utils/tradeDraftRequestCards";

const CARDS_PER_PAGE = 50;
const SEARCH_MIN_LENGTH = 3;
const FILTER_META_DEFAULT = {
  editions: [],
  languages: [],
  conditions: []
};
const FOIL_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "nonfoil", label: "Non-foil only" },
  { value: "foil", label: "Foil only" }
];
const ALTERED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "notaltered", label: "Not altered" },
  { value: "altered", label: "Altered only" }
];
const SIGNED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "unsigned", label: "Not signed" },
  { value: "signed", label: "Signed only" }
];

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function createPaginationState(page = 1) {
  return {
    page,
    limit: CARDS_PER_PAGE,
    totalItems: 0,
    totalPages: 1,
    totalCards: 0
  };
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis-right", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis-left", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis-left", currentPage - 1, currentPage, currentPage + 1, "ellipsis-right", totalPages];
}

function PaginationControls({ currentPage, totalItems, totalPages, onPageChange }) {
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * CARDS_PER_PAGE + 1;
  const endItem = Math.min(totalItems, currentPage * CARDS_PER_PAGE);
  const pageNumbers = getVisiblePageNumbers(currentPage, totalPages);

  const commitPageInput = () => {
    const nextPage = Number.parseInt(pageInput, 10);

    if (!Number.isFinite(nextPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const clampedPage = Math.min(Math.max(nextPage, 1), totalPages);
    setPageInput(String(clampedPage));

    if (clampedPage !== currentPage) {
      onPageChange(clampedPage);
    }
  };

  return (
    <div className="app-pagination">
      <div className="app-pagination__summary">
        Showing {startItem}-{endItem} of {totalItems} matching listings
      </div>
      <div className="app-pagination__controls">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
          title="Previous page"
          disabled={currentPage === 1}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage === 1 ? "#f8fafc" : "#ffffff",
            color: currentPage === 1 ? "#94a3b8" : "#0f172a",
            cursor: currentPage === 1 ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{"<"}</span>
        </button>
        <div className="app-pagination__page-list">
          {pageNumbers.map((pageNumber) => {
            if (typeof pageNumber !== "number") {
              return <span key={pageNumber} className="app-pagination__ellipsis">...</span>;
            }

            const isActive = pageNumber === currentPage;

            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => onPageChange(pageNumber)}
                aria-current={isActive ? "page" : undefined}
                className="app-pagination__button"
                style={{
                  border: isActive ? "1px solid #0f766e" : "1px solid #cbd5e1",
                  background: isActive ? "#ccfbf1" : "#ffffff",
                  color: isActive ? "#115e59" : "#334155"
                }}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
          title="Next page"
          disabled={currentPage === totalPages}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage === totalPages ? "#f8fafc" : "#ffffff",
            color: currentPage === totalPages ? "#94a3b8" : "#0f172a",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{">"}</span>
        </button>
        <label className="app-pagination__jump">
          Page
          <div className="app-pagination__jump-controls">
            <div className="app-pagination__select-shell">
              <input
                type="number"
                min="1"
                max={String(totalPages)}
                step="1"
                inputMode="numeric"
                aria-label="Jump to page"
                className="app-pagination__page-input"
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitPageInput();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    setPageInput(String(currentPage));
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="app-pagination__button app-pagination__button--text app-pagination__jump-button"
              onClick={commitPageInput}
            >
              Go
            </button>
          </div>
        </label>
      </div>
    </div>
  );
}

function buildFallbackFilterMeta(cards = []) {
  const editions = [];
  const editionCodes = new Set();
  const languages = new Set();
  const conditions = new Set();

  cards.forEach((card) => {
    const edition = String(card?.edition || "").trim();
    const editionCode = edition.toLowerCase();

    if (edition && !editionCodes.has(editionCode)) {
      editionCodes.add(editionCode);
      editions.push({ code: edition, label: edition.toUpperCase() });
    }

    if (card?.language) {
      languages.add(String(card.language).toLowerCase());
    }

    if (card?.condition) {
      conditions.add(String(card.condition).toUpperCase());
    }
  });

  return {
    editions,
    languages: [...languages].sort(),
    conditions: [...conditions].sort()
  };
}

function getTradeDraftCardTokens(card = {}) {
  const tokens = new Set();
  const numericListingId = Number(card.source_listing_id || card.id);

  if (Number.isInteger(numericListingId) && numericListingId > 0) {
    tokens.add(`listing:${numericListingId}`);
  }

  tokens.add([
    card.printing_id || "",
    String(card.language || "").trim().toLowerCase(),
    String(card.condition || "").trim().toUpperCase(),
    card.foil ? "foil" : "nonfoil",
    card.altered ? "altered" : "clean",
    card.signed ? "signed" : "unsigned",
    String(card.card || "").trim().toLowerCase()
  ].join(":"));

  return tokens;
}

function hasMatchingTradeDraftToken(card, existingTokens) {
  for (const token of getTradeDraftCardTokens(card)) {
    if (existingTokens.has(token)) {
      return true;
    }
  }

  return false;
}

export default function BrowseCardsView() {
  const navigate = useNavigate();
  const { browseCardName } = useParams();
  const { selectedUserId } = useCurrentUser();
  const searchFieldRef = useRef(null);
  const dropdownRef = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [error, setError] = useState(null);
  const [previewCard, setPreviewCard] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [pagination, setPagination] = useState(() => createPaginationState());
  const resultsSectionRef = usePaginationScroll(pagination.page);
  const [filterMeta, setFilterMeta] = useState(FILTER_META_DEFAULT);
  const [setMetadata, setSetMetadata] = useState({});
  const [editionFilter, setEditionFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [foilFilter, setFoilFilter] = useState("all");
  const [alteredFilter, setAlteredFilter] = useState("all");
  const [signedFilter, setSignedFilter] = useState("all");
  const [tradeDrafts, setTradeDrafts] = useState([]);
  const [tradeFinderSuggestions, setTradeFinderSuggestions] = useState([]);
  const [tradeDraftNotice, setTradeDraftNotice] = useState(null);
  const [tradeDraftNoticeCounterpartyUserId, setTradeDraftNoticeCounterpartyUserId] = useState(null);
  const [addingTradeCardKey, setAddingTradeCardKey] = useState("");

  const routeCardName = String(browseCardName || "").trim();
  const hasSelectedRouteCard = routeCardName.length >= SEARCH_MIN_LENGTH;
  const trimmedSearchInput = searchInput.trim();
  const hasCommittedSearch = searchTerm.length >= SEARCH_MIN_LENGTH;
  const activeFilterCount = [
    Boolean(editionFilter),
    Boolean(languageFilter),
    Boolean(conditionFilter),
    foilFilter !== "all",
    alteredFilter !== "all",
    signedFilter !== "all"
  ].filter(Boolean).length;

  const filteredCards = useMemo(() => cards.filter((card) => {
    if (editionFilter && String(card.edition || "").toLowerCase() !== String(editionFilter).toLowerCase()) {
      return false;
    }

    if (languageFilter && String(card.language || "").toLowerCase() !== String(languageFilter).toLowerCase()) {
      return false;
    }

    if (conditionFilter && String(card.condition || "").toUpperCase() !== String(conditionFilter).toUpperCase()) {
      return false;
    }

    if (foilFilter === "foil" && !Boolean(card.foil)) {
      return false;
    }

    if (foilFilter === "nonfoil" && Boolean(card.foil)) {
      return false;
    }

    if (alteredFilter === "altered" && !Boolean(card.altered)) {
      return false;
    }

    if (alteredFilter === "notaltered" && Boolean(card.altered)) {
      return false;
    }

    if (signedFilter === "signed" && !Boolean(card.signed)) {
      return false;
    }

    if (signedFilter === "unsigned" && Boolean(card.signed)) {
      return false;
    }

    return true;
  }), [alteredFilter, cards, conditionFilter, editionFilter, foilFilter, languageFilter, signedFilter]);

  const availableFilterMeta = useMemo(() => {
    const fallbackMeta = buildFallbackFilterMeta(cards);

    return {
      editions: filterMeta.editions?.length ? filterMeta.editions : fallbackMeta.editions,
      languages: filterMeta.languages?.length ? filterMeta.languages : fallbackMeta.languages,
      conditions: filterMeta.conditions?.length ? filterMeta.conditions : fallbackMeta.conditions
    };
  }, [cards, filterMeta.conditions, filterMeta.editions, filterMeta.languages]);

  const suggestionTokensByUserId = useMemo(() => new Map(
    tradeFinderSuggestions.map((suggestion) => [
      Number(suggestion?.counterparty?.id),
      new Set((suggestion?.receiveCards || []).flatMap((card) => Array.from(getTradeDraftCardTokens(card))))
    ])
  ), [tradeFinderSuggestions]);

  const draftTokensByUserId = useMemo(() => new Map(
    tradeDrafts.map((draft) => [
      Number(draft?.counterparty?.id),
      new Set((draft?.demandCards || []).flatMap((card) => Array.from(getTradeDraftCardTokens(card))))
    ])
  ), [tradeDrafts]);

  const tradeDraftsByUserId = useMemo(() => new Map(
    tradeDrafts.map((draft) => [Number(draft?.counterparty?.id), draft])
  ), [tradeDrafts]);

  const tradeFinderSuggestionsByUserId = useMemo(() => new Map(
    tradeFinderSuggestions.map((suggestion) => [Number(suggestion?.counterparty?.id), suggestion])
  ), [tradeFinderSuggestions]);

  const refreshTradeDrafts = async () => {
    const response = await apiFetch("/api/trade-drafts", { userId: selectedUserId });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || "Could not load trade drafts");
    }

    setTradeDrafts(Array.isArray(data.drafts) ? data.drafts : []);
  };

  const getTradeActionState = (card) => {
    const counterpartyUserId = Number(card?.user_id);

    if (!Number.isInteger(counterpartyUserId) || counterpartyUserId < 1) {
      return { disabled: true, label: "Unavailable", tooltip: "Unavailable" };
    }

    if (counterpartyUserId === Number(selectedUserId)) {
      return { disabled: true, label: "Your card", tooltip: "You cannot trade your own cards" };
    }

    const existingSuggestion = tradeFinderSuggestionsByUserId.get(counterpartyUserId) || null;
    const existingDraft = tradeDraftsByUserId.get(counterpartyUserId) || null;
    const effectiveDemandCards = mergeTradeDraftRequestCards(
      existingDraft?.demandCards || [],
      existingSuggestion?.receiveCards || []
    );
    const requestedQty = getRequestedTradeDraftQty(effectiveDemandCards, card);
    const availableQty = Math.max(1, Number(card?.qty) || 1);
    const remainingQty = Math.max(0, availableQty - requestedQty);

    if (requestedQty >= availableQty && hasMatchingTradeDraftToken(card, suggestionTokensByUserId.get(counterpartyUserId) || new Set()) && !existingDraft) {
      return { disabled: true, label: "In suggestion", tooltip: "Already in suggested trade", maxQty: 0, availableQty: remainingQty };
    }

    if (requestedQty >= availableQty && hasMatchingTradeDraftToken(card, draftTokensByUserId.get(counterpartyUserId) || new Set())) {
      return { disabled: true, label: "In draft", tooltip: "Already in trade draft", maxQty: 0, availableQty: remainingQty };
    }

    return { disabled: false, label: "Add to trade", tooltip: "", maxQty: Math.max(1, remainingQty), availableQty: remainingQty };
  };

  const handleAddCardToTrade = async (card, requestedQty = 1) => {
    const counterpartyUserId = Number(card?.user_id);
    const listingId = Number(card?.source_listing_id || card?.id);
    const availableQty = Math.max(1, Number(card?.qty) || 1);
    const nextQty = Math.min(Math.max(1, Number.parseInt(String(requestedQty ?? 1), 10) || 1), availableQty);

    if (!Number.isInteger(counterpartyUserId) || counterpartyUserId < 1 || !Number.isInteger(listingId) || listingId < 1) {
      return;
    }

    const tradeCardKey = `${counterpartyUserId}:${listingId}`;
    const existingSuggestion = tradeFinderSuggestionsByUserId.get(counterpartyUserId) || null;
    const existingDraft = tradeDraftsByUserId.get(counterpartyUserId) || null;
    const hasSuggestedTrade = Boolean(existingSuggestion);
    const nextOfferCards = hasSuggestedTrade
      ? mergeTradeDraftRequestCards(existingDraft?.offerCards || [], existingSuggestion.sendCards || [])
      : existingDraft
        ? (existingDraft.offerCards || []).map(serializeTradeDraftCardForRequest)
        : [];
    const baseDemandCards = hasSuggestedTrade
      ? mergeTradeDraftRequestCards(existingDraft?.demandCards || [], existingSuggestion.receiveCards || [])
      : mergeTradeDraftRequestCards(existingDraft?.demandCards || []);
    const nextDemandCards = baseDemandCards.length > 0
      ? addTradeDraftRequestCard(baseDemandCards, card, nextQty)
      : null;

    try {
      setAddingTradeCardKey(tradeCardKey);
      const response = await apiFetch(`/api/trade-drafts/${counterpartyUserId}/demand-cards`, {
        userId: selectedUserId,
        method: nextDemandCards ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: nextDemandCards
          ? JSON.stringify({
              offerCards: nextOfferCards,
              demandCards: nextDemandCards
            })
          : JSON.stringify({ listingId, qty: nextQty })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not add card to trade");
      }

      await refreshTradeDrafts();
      setTradeDraftNotice(
        hasSuggestedTrade
          ? `A suggested trade already existed. ${card.card || "Card"} was added to that suggested trade in Drafts. The draft expires after 4 hours if it is not proposed.`
          : `${card.card || "Card"} was added to the trade draft. The draft expires after 4 hours if it is not proposed.`
      );
      setTradeDraftNoticeCounterpartyUserId(counterpartyUserId);
    } catch (addError) {
      console.error("Browse cards add to trade error:", addError);
      setTradeDraftNotice(addError.message || "Could not add card to trade");
      setTradeDraftNoticeCounterpartyUserId(counterpartyUserId);
    } finally {
      setAddingTradeCardKey("");
    }
  };

  useEffect(() => {
    let isDisposed = false;

    const loadTradeContext = async () => {
      try {
        const [suggestionsResponse, draftsResponse] = await Promise.all([
          apiFetch(`/api/tradefinder/${selectedUserId}`, { userId: selectedUserId }),
          apiFetch("/api/trade-drafts", { userId: selectedUserId })
        ]);
        const [suggestionsData, draftsData] = await Promise.all([
          suggestionsResponse.json().catch(() => ({})),
          draftsResponse.json().catch(() => ({}))
        ]);

        if (!suggestionsResponse.ok) {
          throw new Error(suggestionsData.error || "Could not load trade suggestions");
        }

        if (!draftsResponse.ok) {
          throw new Error(draftsData.error || "Could not load trade drafts");
        }

        if (!isDisposed) {
          setTradeFinderSuggestions(Array.isArray(suggestionsData.suggestions) ? suggestionsData.suggestions : []);
          setTradeDrafts(Array.isArray(draftsData.drafts) ? draftsData.drafts : []);
        }
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse cards trade context load error:", loadError);
          setTradeFinderSuggestions([]);
          setTradeDrafts([]);
        }
      }
    };

    loadTradeContext();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  useEffect(() => {
    const setCodes = [...new Set(
      (availableFilterMeta.editions || [])
        .map((edition) => normalizeLookupValue(edition?.code))
        .filter(Boolean)
    )];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`/api/traders/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId: selectedUserId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (!isCancelled) {
          setSetMetadata((current) => ({
            ...current,
            ...(data.sets || {})
          }));
        }
      })
      .catch((loadError) => {
        console.error("Browse cards set metadata error:", loadError);
      });

    return () => {
      isCancelled = true;
    };
  }, [availableFilterMeta.editions, selectedUserId, setMetadata]);

  useEffect(() => {
    if (!routeCardName) {
      setSearchInput("");
      setSearchTerm("");
      setPreviewCard(null);
      return;
    }

    setSearchInput(routeCardName);
    setSearchTerm(routeCardName);
  }, [routeCardName]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setDropdownStyle(null);
      return undefined;
    }

    const updateDropdownPosition = () => {
      const element = searchFieldRef.current;

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(180, window.innerHeight - rect.bottom - 24),
        zIndex: 1400
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isDropdownOpen, suggestions.length, loadingSuggestions, suggestionsError]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (searchFieldRef.current?.contains(event.target) || dropdownRef.current?.contains(event.target)) {
        return;
      }

      setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isDropdownOpen]);

  useEffect(() => {
    let isDisposed = false;

    const loadSuggestions = async () => {
      try {
        if (trimmedSearchInput.length < SEARCH_MIN_LENGTH) {
          setSuggestions([]);
          setLoadingSuggestions(false);
          setSuggestionsError("");
          return;
        }

        setLoadingSuggestions(true);
        setSuggestionsError("");

        const response = await apiFetch(`/api/traders/search/cards?q=${encodeURIComponent(trimmedSearchInput)}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not search cards");
        }

        if (!isDisposed) {
          setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        }
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse cards suggestions error:", loadError);
          setSuggestions([]);
          setSuggestionsError(loadError.message || "Could not search cards");
        }
      } finally {
        if (!isDisposed) {
          setLoadingSuggestions(false);
        }
      }
    };

    loadSuggestions();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId, trimmedSearchInput]);

  useEffect(() => {
    setPagination(createPaginationState(1));
  }, [searchTerm, editionFilter, languageFilter, conditionFilter, foilFilter, alteredFilter, signedFilter]);

  useEffect(() => {
    if (!hasCommittedSearch) {
      setCards([]);
      setError(null);
      setPreviewCard(null);
      setPagination(createPaginationState());
      setFilterMeta(FILTER_META_DEFAULT);
      return undefined;
    }

    let isDisposed = false;

    const loadCards = async () => {
      try {
        setLoadingCards(true);
        setError(null);

        const params = new URLSearchParams({
          page: String(pagination.page),
          limit: String(CARDS_PER_PAGE),
          search: searchTerm
        });

        if (hasSelectedRouteCard) params.set("exactCard", "true");

        if (editionFilter) params.set("edition", editionFilter);
        if (languageFilter) params.set("language", languageFilter);
        if (conditionFilter) params.set("condition", conditionFilter);
        if (foilFilter !== "all") params.set("foil", foilFilter);
        if (alteredFilter !== "all") params.set("altered", alteredFilter);
        if (signedFilter !== "all") params.set("signed", signedFilter);

        const response = await apiFetch(`/api/traders/browse/cards?${params.toString()}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load browse cards");
        }

        if (!isDisposed) {
          setCards(Array.isArray(data.cards) ? data.cards : []);
          setPagination(data.pagination || createPaginationState(pagination.page));
          setFilterMeta(data.filterMeta || FILTER_META_DEFAULT);
        }
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse cards load error:", loadError);
          setError(loadError.message || "Could not load browse cards");
          setCards([]);
        }
      } finally {
        if (!isDisposed) {
          setLoadingCards(false);
        }
      }
    };

    loadCards();

    return () => {
      isDisposed = true;
    };
  }, [alteredFilter, conditionFilter, editionFilter, foilFilter, hasCommittedSearch, hasSelectedRouteCard, languageFilter, pagination.page, searchTerm, selectedUserId, signedFilter]);

  useEffect(() => {
    if (!hasCommittedSearch) {
      setPreviewCard(null);
      setLoadingPreview(false);
      return undefined;
    }

    let isDisposed = false;

    const loadPreview = async () => {
      try {
        setLoadingPreview(true);

        const previewFromResults = filteredCards[0] || null;

        if (previewFromResults) {
          setPreviewCard({
            card: previewFromResults.card || searchTerm,
            image: previewFromResults.imageNormal || previewFromResults.image || "",
            priceTrend: previewFromResults.price_trend ?? null,
            priceFoil: previewFromResults.price_foil ?? null
          });
          setLoadingPreview(false);
          return;
        }

        const response = await apiFetch(`/api/traders/search/prints?card=${encodeURIComponent(searchTerm)}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load card preview");
        }

        if (isDisposed) {
          return;
        }

        const prints = Array.isArray(data.prints) ? data.prints : [];
        const matchingPrinting = prints.find((printing) => {
          if (editionFilter && String(printing.setCode || "").toLowerCase() !== String(editionFilter).toLowerCase()) {
            return false;
          }

          if (foilFilter === "foil" && !printing.foil) {
            return false;
          }

          if (foilFilter === "nonfoil" && !printing.nonfoil) {
            return false;
          }

          return true;
        }) || prints[0] || null;

        setPreviewCard(matchingPrinting ? {
          card: searchTerm,
          image: matchingPrinting.imageNormal || matchingPrinting.image || "",
          priceTrend: matchingPrinting.priceTrend ?? null,
          priceFoil: matchingPrinting.priceFoil ?? null
        } : null);
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse cards preview error:", loadError);
          setPreviewCard(null);
        }
      } finally {
        if (!isDisposed) {
          setLoadingPreview(false);
        }
      }
    };

    loadPreview();

    return () => {
      isDisposed = true;
    };
  }, [editionFilter, filteredCards, foilFilter, hasCommittedSearch, searchTerm, selectedUserId]);

  const shouldShowDropdown = isDropdownOpen && (trimmedSearchInput.length >= SEARCH_MIN_LENGTH || loadingSuggestions || suggestionsError || suggestions.length > 0);
  const dropdown = shouldShowDropdown && dropdownStyle
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            ...dropdownStyle,
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: "14px",
            boxShadow: "0 18px 30px rgba(15, 23, 42, 0.12)",
            overflowY: "auto"
          }}
        >
          {loadingSuggestions ? <div style={{ padding: "12px 14px", color: "#64748b", fontSize: "14px" }}>Searching cards...</div> : null}
          {!loadingSuggestions && suggestionsError ? <div style={{ padding: "12px 14px", color: "#b91c1c", fontSize: "14px" }}>{suggestionsError}</div> : null}
          {!loadingSuggestions && !suggestionsError && suggestions.length === 0 && trimmedSearchInput.length >= SEARCH_MIN_LENGTH ? (
            <div style={{ padding: "12px 14px", color: "#64748b", fontSize: "14px" }}>No cards found.</div>
          ) : null}
          {!loadingSuggestions && !suggestionsError && suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => {
                navigate(`/browse/cards/${encodeURIComponent(suggestion)}`);
                setIsDropdownOpen(false);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px 14px",
                border: "none",
                background: "#fff",
                cursor: "pointer",
                fontSize: "14px",
                borderTop: index === 0 ? "none" : "1px solid #f1f5f9"
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  const handleSearchSubmit = () => {
    if (trimmedSearchInput.length < SEARCH_MIN_LENGTH) {
      return;
    }

    navigate(`/browse/cards/${encodeURIComponent(trimmedSearchInput)}`);
    setIsDropdownOpen(false);
  };

  const resetSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setSuggestions([]);
    setSuggestionsError("");
    setIsDropdownOpen(false);
    setEditionFilter("");
    setLanguageFilter("");
    setConditionFilter("");
    setFoilFilter("all");
    setAlteredFilter("all");
    setSignedFilter("all");
    setPreviewCard(null);
    navigate("/browse/cards");
  };

  const clearFilters = () => {
    setEditionFilter("");
    setLanguageFilter("");
    setConditionFilter("");
    setFoilFilter("all");
    setAlteredFilter("all");
    setSignedFilter("all");
  };

  const filterFields = (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
      <label className="browse-cards-field">
        Edition
        <AccountSettingSelect
          title="Edition"
          value={editionFilter}
          options={[
            { value: "", label: "All editions" },
            ...(availableFilterMeta.editions || []).map((edition) => ({
              value: edition.code,
              label: `${setMetadata[normalizeLookupValue(edition.code)]?.name || edition.label || String(edition.code || "").toUpperCase()}${edition.code ? ` (${String(edition.code).toUpperCase()})` : ""}`,
              setCode: normalizeLookupValue(edition.code),
              setMetadata: setMetadata[normalizeLookupValue(edition.code)],
              showSetSymbol: Boolean(edition.code)
            }))
          ]}
          onChange={(value) => {
            setPagination(createPaginationState(1));
            setEditionFilter(value);
          }}
          renderSelectedValue={(nextValue, option) => renderEditionOption(nextValue, option, "All editions")}
          renderOption={(nextValue, option) => renderEditionOption(nextValue, option)}
        />
      </label>
      <label className="browse-cards-field">
        Language
        <AccountSettingSelect
          title="Language"
          value={languageFilter}
          options={[
            { value: "", label: "All languages" },
            ...(availableFilterMeta.languages || []).map((language) => ({ value: language, label: language }))
          ]}
          onChange={(value) => {
            setPagination(createPaginationState(1));
            setLanguageFilter(value);
          }}
          renderSelectedValue={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
          renderOption={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
        />
      </label>
      <label className="browse-cards-field">
        Condition
        <AccountSettingSelect
          title="Condition"
          value={conditionFilter}
          options={[
            { value: "", label: "All conditions" },
            ...(availableFilterMeta.conditions || []).map((condition) => ({ value: condition, label: condition }))
          ]}
          onChange={(value) => {
            setPagination(createPaginationState(1));
            setConditionFilter(value);
          }}
          renderSelectedValue={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
          renderOption={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
        />
      </label>
      <label className="browse-cards-field">
        Foil
        <AccountSettingSelect
          title="Foil"
          value={foilFilter}
          options={FOIL_FILTER_OPTIONS}
          onChange={(value) => {
            setPagination(createPaginationState(1));
            setFoilFilter(value);
          }}
        />
      </label>
      <label className="browse-cards-field">
        Altered
        <AccountSettingSelect
          title="Altered"
          value={alteredFilter}
          options={ALTERED_FILTER_OPTIONS}
          onChange={(value) => {
            setPagination(createPaginationState(1));
            setAlteredFilter(value);
          }}
        />
      </label>
      <label className="browse-cards-field">
        Signed
        <AccountSettingSelect
          title="Signed"
          value={signedFilter}
          options={SIGNED_FILTER_OPTIONS}
          onChange={(value) => {
            setPagination(createPaginationState(1));
            setSignedFilter(value);
          }}
        />
      </label>
    </div>
  );

  return (
    <div className="account-shell app-responsive-shell app-responsive-stack browse-cards-page">
      <div className="account-settings-page" style={{ paddingBottom: "4px" }}>
        <div className="account-breadcrumb">
          <Link to="/browse/cards">Browse cards</Link>
          {hasCommittedSearch ? (
            <span className="browse-cards-breadcrumb-current">
              <span>{"/"}</span>
              <span>{searchTerm}</span>
            </span>
          ) : null}
        </div>

        <div className="account-settings-panel__header">
          <div>
            <h1 className="account-title">{hasCommittedSearch ? searchTerm : "Browse cards"}</h1>
            <p className="account-copy">
              Search for cards to find traders who have matching listings. 
            </p>
          </div>
        </div>
      </div>

      {error ? (
        <div className="browse-cards-alert browse-cards-alert--error">
          {error}
        </div>
      ) : null}

      {tradeDraftNotice ? (
        <div className="browse-cards-alert browse-cards-alert--success browse-cards-alert--floating">
          <span>{tradeDraftNotice}</span>
          {tradeDraftNoticeCounterpartyUserId ? (
            <button
              type="button"
              onClick={() => navigate(`/tradefinder?counterpartyUserId=${tradeDraftNoticeCounterpartyUserId}`)}
              className="browse-cards-cta"
            >
              Open TradeFinder
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="app-responsive-split browse-cards-layout">
        <aside className="browse-cards-sidebar">
          <div className="browse-cards-sidebar-card">
            <div className="browse-cards-sidebar-title">Find card</div>
            <div ref={searchFieldRef} style={{ position: "relative" }}>
              <input
                type="search"
                value={searchInput}
                placeholder="Type at least 3 letters"
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSearchSubmit();
                  }
                }}
                className="browse-cards-search-input"
              />
            </div>
            {dropdown}
            <div className="browse-cards-sidebar-actions">
              <button
                type="button"
                onClick={handleSearchSubmit}
                disabled={trimmedSearchInput.length < SEARCH_MIN_LENGTH}
                className="browse-cards-primary-button"
                style={{
                  background: trimmedSearchInput.length < SEARCH_MIN_LENGTH ? "#cbd5e1" : "#0f766e",
                  cursor: trimmedSearchInput.length < SEARCH_MIN_LENGTH ? "not-allowed" : "pointer"
                }}
              >
                Search
              </button>
              <button
                type="button"
                onClick={resetSearch}
                className="browse-cards-secondary-button"
              >
                Reset
              </button>
            </div>
            <div className="browse-cards-sidebar-meta">
              {trimmedSearchInput.length < SEARCH_MIN_LENGTH
                ? "Type 3 letters to see card suggestions"
                : loadingSuggestions
                  ? "Loading cards..."
                  : `${suggestions.length} suggestions`}
            </div>
          </div>
        </aside>

        <section className="browse-cards-content">
          {!hasCommittedSearch ? (
            <div className="browse-cards-empty-state">
              Search for a card to list every trader who currently has matching copies for sale.
            </div>
          ) : (
            <>
              <div className={`browse-cards-preview-panel${previewCard?.image ? " browse-cards-preview-panel--with-image" : ""}`}>
                <div className="browse-cards-filter-eyebrow browse-cards-preview-eyebrow">
                  Card detail
                </div>
                {previewCard?.image ? (
                  <div className="browse-cards-preview-image-card">
                    <div className="card-tile-image-shell is-loaded browse-cards-preview-image-shell">
                      <img
                        src={previewCard.image}
                        alt={previewCard.card}
                        className="card-tile-image is-loaded browse-cards-preview-image"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="browse-cards-preview-copy">
                  <div className="browse-cards-preview-meta">
                    {loadingPreview ? <div>Loading card preview...</div> : null}
                    <div><strong>Market matches:</strong> {filteredCards.length}</div>
                    {previewCard?.priceTrend !== null && previewCard?.priceTrend !== undefined ? <div><strong>Trend:</strong> {Number(previewCard.priceTrend).toFixed(2)} €</div> : null}
                    {previewCard?.priceFoil !== null && previewCard?.priceFoil !== undefined ? <div><strong>Foil Trend:</strong> {Number(previewCard.priceFoil).toFixed(2)} €</div> : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsAdvancedFiltersOpen(true)}
                    className="browse-cards-secondary-button browse-cards-filter-trigger"
                    style={{
                      borderColor: activeFilterCount > 0 ? "#0f766e" : undefined,
                      background: activeFilterCount > 0 ? "#ccfbf1" : "#ffffff",
                      color: activeFilterCount > 0 ? "#115e59" : "#334155"
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M2 3.5h12l-4.6 5.2v3.2l-2.8 1.6V8.7L2 3.5Z" fill="currentColor" />
                    </svg>
                    {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filters"}
                  </button>
                </div>
              </div>

              <section className="browse-cards-results-section" ref={resultsSectionRef}>
                {loadingCards ? (
                  <div className="browse-cards-empty-state">
                    Loading matching listings...
                  </div>
                ) : filteredCards.length === 0 ? (
                  <div className="browse-cards-empty-state">
                    No active listings match the selected card and filters.
                  </div>
                ) : (
                  <>
                    <PaginationControls
                      currentPage={pagination.page}
                      totalItems={filteredCards.length}
                      totalPages={pagination.totalPages}
                      onPageChange={(nextPage) => setPagination((current) => ({ ...current, page: nextPage }))}
                    />
                    <div className="app-responsive-table browse-cards-results-table">
                      <BrowseCardsResultsTable
                        cards={filteredCards}
                        userId={selectedUserId}
                        getTradeActionState={getTradeActionState}
                        onAddToTrade={handleAddCardToTrade}
                        addingTradeCardKey={addingTradeCardKey}
                      />
                    </div>
                    <PaginationControls
                      currentPage={pagination.page}
                      totalItems={filteredCards.length}
                      totalPages={pagination.totalPages}
                      onPageChange={(nextPage) => setPagination((current) => ({ ...current, page: nextPage }))}
                    />
                  </>
                )}
              </section>
            </>
          )}
        </section>
      </div>

      <AdvancedFiltersModal
        isOpen={isAdvancedFiltersOpen}
        onClose={() => setIsAdvancedFiltersOpen(false)}
        title="Browse card filters"
        activeCount={activeFilterCount}
        onClear={clearFilters}
      >
        {filterFields}
      </AdvancedFiltersModal>

    </div>
  );
}